import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { S3Client, PutObjectCommand } from '@aws-sdk/client-s3';
import { renderToStaticMarkup } from 'react-dom/server';
import PdfRenderComponent from './PdfRenderComponent';

// Helper function to convert image URL to base64
const getImageAsBase64 = async (url) => {
    try {
        // Check if it's already a data URL
        if (url.startsWith('data:')) {
            return url;
        }

        const response = await fetch(url, {
            mode: 'cors',
            credentials: 'omit'
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch image: ${response.status}`);
        }

        const blob = await response.blob();

        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    } catch (error) {
        console.warn('Failed to convert image to base64:', error);
        return null;
    }
};

// Function to preload and convert all images in the data to base64
const preprocessImageData = async (data) => {
    const processedData = JSON.parse(JSON.stringify(data)); // Deep clone

    // Process profile image if it exists
    if (processedData.img_url && typeof processedData.img_url === 'string' && !processedData.img_url.startsWith('data:')) {
        const base64Image = await getImageAsBase64(processedData.img_url);
        if (base64Image) {
            processedData.img_url = base64Image;
        }
    }

    // Process any other image fields that might exist
    const imageFields = ['profile_image', 'photo', 'image_url'];
    for (const field of imageFields) {
        if (processedData[field] && typeof processedData[field] === 'string' && !processedData[field].startsWith('data:')) {
            const base64Image = await getImageAsBase64(processedData[field]);
            if (base64Image) {
                processedData[field] = base64Image;
            }
        }
    }

    return processedData;
};

export const generateAndUploadPdf = async (
    customTitle,
    type,
    folderPath = 'Peace Village/patient',
    { relativesList, userDatas, recommend1, recommend2, emergency, formName }
) => {
    try {
        // Preprocess image data to convert URLs to base64
        const processedUserDatas = await preprocessImageData(userDatas);
        const processedRelativesList = relativesList ? await Promise.all(
            relativesList.map(relative => preprocessImageData(relative))
        ) : relativesList;

        // Create a temporary container for rendering
        const tempContainer = document.createElement('div');
        tempContainer.style.position = 'absolute';
        tempContainer.style.left = '-9999px';
        tempContainer.style.top = '-9999px';
        tempContainer.style.width = '794px'; // A4 width in pixels at 96 DPI
        tempContainer.style.backgroundColor = 'white';
        document.body.appendChild(tempContainer);

        const staticMarkup = renderToStaticMarkup(
            <PdfRenderComponent
                relativesList={processedRelativesList}
                userDatas={processedUserDatas}
                recommend1={recommend1}
                recommend2={recommend2}
                emergency={emergency}
                formName={formName}
                isPdfMode={true}
            />
        );

        // Set the innerHTML of our temp container
        tempContainer.innerHTML = staticMarkup;

        // Wait for images to load
        const images = tempContainer.querySelectorAll('img');
        const imageLoadPromises = Array.from(images).map(img => {
            return new Promise((resolve) => {
                if (img.complete) {
                    resolve();
                } else {
                    img.onload = resolve;
                    img.onerror = resolve; // Continue even if image fails to load
                    // Set a timeout to prevent hanging
                    setTimeout(resolve, 5000);
                }
            });
        });

        await Promise.all(imageLoadPromises);

        // Store original document title
        const originalTitle = document.title;
        document.title = customTitle;

        const canvas = await html2canvas(tempContainer.firstChild, {
            scale: 2, // Increased scale for better quality
            logging: false,
            useCORS: true,
            allowTaint: false, // Changed to false for better CORS handling
            windowHeight: tempContainer.firstChild.scrollHeight,
            backgroundColor: '#FFFFFF',
            imageTimeout: 10000, // 10 second timeout for images
            onclone: (clonedDoc) => {
                clonedDoc.body.style.backgroundColor = 'white';
                clonedDoc.querySelectorAll('*').forEach(el => {
                    el.style.boxShadow = 'none';
                    el.style.textShadow = 'none';
                });

                // Ensure images in cloned document are also processed
                clonedDoc.querySelectorAll('img').forEach(img => {
                    img.style.maxWidth = '100%';
                    img.style.height = 'auto';
                });
            }
        });

        // Clean up - remove the temporary container
        document.body.removeChild(tempContainer);

        // Restore original title
        document.title = originalTitle;

        // Create PDF with better quality settings
        const pdf = new jsPDF('p', 'mm', 'a4');

        // PDF dimensions
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();
        const margin = 10; // 10mm margins
        const contentWidth = pdfWidth - (2 * margin);
        const contentHeight = pdfHeight - (2 * margin);

        // Calculate how the image should fit
        const imgAspectRatio = canvas.width / canvas.height;
        const pageAspectRatio = contentWidth / contentHeight;

        let imgWidth, imgHeight;

        if (imgAspectRatio > pageAspectRatio) {
            // Image is wider, fit to width
            imgWidth = contentWidth;
            imgHeight = contentWidth / imgAspectRatio;
        } else {
            // Image is taller, fit to height or use actual proportions
            imgWidth = contentWidth;
            imgHeight = contentWidth / imgAspectRatio;
        }

        // Calculate how many pages we need
        const totalPages = Math.ceil(imgHeight / contentHeight);

        // Add pages to the PDF
        for (let pageIndex = 0; pageIndex < totalPages; pageIndex++) {
            if (pageIndex > 0) {
                pdf.addPage();
            }

            // Calculate the portion of the image to show on this page
            const yOffset = pageIndex * contentHeight;
            const remainingImageHeight = imgHeight - yOffset;
            const currentPageImageHeight = Math.min(remainingImageHeight, contentHeight);

            // Only add the image if there's content to show
            if (currentPageImageHeight > 0) {
                // Create a temporary canvas for this page's content
                const pageCanvas = document.createElement('canvas');
                const pageCtx = pageCanvas.getContext('2d');

                // Set canvas size based on the content we want to show
                const scaleFactor = canvas.width / imgWidth;
                pageCanvas.width = canvas.width;
                pageCanvas.height = currentPageImageHeight * scaleFactor;

                // Clear the canvas with white background
                pageCtx.fillStyle = '#FFFFFF';
                pageCtx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);

                // Draw the portion of the original image
                pageCtx.drawImage(
                    canvas,
                    0, yOffset * scaleFactor, // Source x, y
                    canvas.width, pageCanvas.height, // Source width, height
                    0, 0, // Destination x, y
                    pageCanvas.width, pageCanvas.height // Destination width, height
                );

                // Convert this page to image and add to PDF
                const pageImgData = pageCanvas.toDataURL('image/jpeg', 0.95);

                pdf.addImage(
                    pageImgData,
                    'JPEG',
                    margin, // x position
                    margin, // y position
                    contentWidth, // width
                    currentPageImageHeight // height
                );
            }
        }

        // Get PDF as Uint8Array
        const pdfData = pdf.output('arraybuffer');

        const s3Client = new S3Client({
            region: 'ap-southeast-1',
            credentials: {
                accessKeyId: "Y0WWU13PSR9JMDUASN8V",
                secretAccessKey: "DF7tATk9SPB0aGkf8tzXkYcfHaBhl2BQzp3hRcm1",
            },
            endpoint: "https://s3.ap-southeast-1.wasabisys.com",
            forcePathStyle: true
        });

        const fileName = customTitle.endsWith('.pdf') ? customTitle : `${customTitle}.pdf`;
        const filePath = `${folderPath}/${customTitle}/${type}/${fileName}`;

        const params = {
            Bucket: "pvdev",
            Key: filePath,
            Body: pdfData,
            ContentType: 'application/pdf',
            ACL: 'private'
        };

        const uploadResponse = await s3Client.send(new PutObjectCommand(params));

        if (uploadResponse.$metadata.httpStatusCode !== 200) {
            throw new Error('Wasabi upload failed');
        }

        // Construct the URL manually
        const fileUrl = `https://s3.ap-southeast-1.wasabisys.com/pvdev/${filePath}`;

        return {
            success: true,
            fileUrl,
            bucket: "pvdev",
            key: filePath,
            fileName
        };
    } catch (error) {
        console.error('PDF generation/upload failed:', error);
        return {
            success: false,
            error: error.message,
            stack: error.stack
        };
    }
};